/**
 * @fileoverview Structural equality for serialized domain values.
 * @module core-domain
 *
 * Responsibilities:
 * - Compare nested arrays and plain objects without relying on key order.
 * Dependencies: None.
 * Architecture: Pure shared utility with no host or persistence dependencies.
 * Notes: Supports JSON-shaped values and Date instances used in domain snapshots.
 */

/**
 * @process isDeepEqual
 * Steps: Compare primitives, dates, arrays, and plain-object entries recursively.
 * Inputs: Two unknown values.
 * Outputs: True when the supported values are structurally equal.
 * Failure Handling: Unsupported non-plain objects compare equal only by identity.
 * Idempotency: REQUIRED.
 */
export function isDeepEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (left instanceof Date || right instanceof Date) {
    return left instanceof Date && right instanceof Date && left.getTime() === right.getTime();
  }
  if (typeof left !== typeof right || left === null || right === null) return false;
  if (Array.isArray(left)) {
    return Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => isDeepEqual(value, right[index]));
  }
  if (Array.isArray(right) || typeof left !== 'object') return false;
  const leftPrototype = Object.getPrototypeOf(left);
  if (leftPrototype !== Object.prototype && leftPrototype !== null) return false;
  if (Object.getPrototypeOf(right) !== leftPrototype) return false;
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  return leftKeys.length === rightKeys.length && leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(rightRecord, key)
    && isDeepEqual(leftRecord[key], rightRecord[key])
  ));
}
